/**
 * SmartDataView - Complete JSON-to-table renderer for all data shapes.
 *
 * Handles:
 * - Flat key-value objects (Field/Value table)
 * - Arrays of objects (delegates to DataTable)
 * - Deeply nested objects (expand/collapse sections)
 * - Arrays of primitives (single-column table)
 * - Mixed-type arrays (hybrid rendering)
 * - Arrays of arrays / matrices (grid table)
 * - Large objects (paginated)
 * - Empty data, nulls, long strings
 *
 * @see https://github.com/rsee301/space-operator-copilot/issues/520
 */

import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import DataTable from './DataTable'
import {
  formatFieldName,
  formatValue,
  classifyValue,
  MAX_STRING_LENGTH,
  PAGE_SIZE,
} from './dataFormatters'
import type { ValueKind } from './dataFormatters'

interface SmartDataViewProps {
  data: unknown
  title: string
  depth?: number
}

function SmartDataView({ data, title, depth = 0 }: SmartDataViewProps) {
  const kind = classifyValue(data)

  // Empty data
  if (kind === 'empty' || kind === 'null') {
    return <EmptyState title={title} />
  }

  // Top-level array routing
  if (Array.isArray(data)) {
    return <ArrayRenderer data={data} title={title} kind={kind} depth={depth} />
  }

  // Object rendering
  if (typeof data === 'object' && data !== null) {
    return <ObjectRenderer data={data as Record<string, unknown>} title={title} depth={depth} />
  }

  // Primitive fallback (shouldn't happen at top level, but safety)
  return (
    <div className="text-xs text-retro-muted font-mono">
      <span className="text-retro-amber uppercase tracking-wider">{title}</span>
      <span className="ml-2">{formatValue(data)}</span>
    </div>
  )
}

/** Renders arrays based on their classified kind */
function ArrayRenderer({
  data,
  title,
  kind,
  depth,
}: {
  data: unknown[]
  title: string
  kind: ValueKind
  depth: number
}) {
  if (kind === 'array-of-objects') {
    return <DataTable data={data as Record<string, unknown>[]} title={title} />
  }

  if (kind === 'array-of-arrays') {
    return <MatrixTable data={data as unknown[][]} title={title} />
  }

  if (kind === 'array-of-primitives') {
    return <PrimitiveList data={data} title={title} />
  }

  // Mixed array: render each item individually
  return <MixedArrayView data={data} title={title} depth={depth} />
}

/** Renders objects with flat fields + nested collapsible sections */
function ObjectRenderer({
  data,
  title,
  depth,
}: {
  data: Record<string, unknown>
  title: string
  depth: number
}) {
  const entries = Object.entries(data)

  if (entries.length === 0) {
    return <EmptyState title={title} />
  }

  // Partition entries into simple (inline) and complex (collapsible)
  const simpleEntries: [string, unknown][] = []
  const complexEntries: [string, Record<string, unknown> | unknown[]][] = []

  for (const [key, value] of entries) {
    const kind = classifyValue(value)
    switch (kind) {
      case 'null':
      case 'primitive':
        simpleEntries.push([key, value])
        break
      case 'simple-object':
        // Flatten into parent with dot notation
        for (const [nestedKey, nestedVal] of Object.entries(value as Record<string, unknown>)) {
          simpleEntries.push([`${key}.${nestedKey}`, nestedVal])
        }
        break
      case 'array-of-primitives':
        complexEntries.push([key, value as unknown[]])
        break
      default:
        complexEntries.push([key, value as Record<string, unknown> | unknown[]])
    }
  }

  return (
    <div className="space-y-3">
      {depth === 0 && (
        <h3 className="text-xs font-mono uppercase tracking-wider text-retro-amber">{title}</h3>
      )}

      {simpleEntries.length > 0 && (
        <PaginatedKeyValueTable entries={simpleEntries} />
      )}

      {complexEntries.map(([key, value]) => (
        <CollapsibleSection key={key} label={formatFieldName(key)} defaultExpanded={depth < 1}>
          <SmartDataView data={value} title={formatFieldName(key)} depth={depth + 1} />
        </CollapsibleSection>
      ))}
    </div>
  )
}

/** Paginated key-value table for flat fields */
function PaginatedKeyValueTable({ entries }: { entries: [string, unknown][] }) {
  const [page, setPage] = useState(0)
  const totalPages = Math.ceil(entries.length / PAGE_SIZE)
  const needsPagination = entries.length > PAGE_SIZE

  const start = page * PAGE_SIZE
  const pageEntries = needsPagination ? entries.slice(start, start + PAGE_SIZE) : entries

  return (
    <div>
      <div className="overflow-x-auto border border-retro-border rounded">
        <table className="w-full text-xs">
          <thead className="bg-retro-bg border-b border-retro-border">
            <tr>
              <th className="px-3 py-2 text-left text-retro-dim uppercase tracking-wider w-1/3">
                Field
              </th>
              <th className="px-3 py-2 text-left text-retro-dim uppercase tracking-wider">
                Value
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-retro-border">
            {pageEntries.map(([key, value], index) => (
              <tr key={index} className="hover:bg-retro-bg/50">
                <td className="px-3 py-2 text-retro-muted font-mono">{formatFieldName(key)}</td>
                <td className="px-3 py-2 text-retro-text font-mono">
                  <CellValue value={value} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {needsPagination && (
        <div className="flex items-center justify-between mt-2 text-xs text-retro-dim">
          <span>
            {start + 1}-{Math.min(start + PAGE_SIZE, entries.length)} of {entries.length} fields
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-2 py-1 rounded border border-retro-border hover:bg-retro-bg disabled:opacity-30"
              aria-label="Previous page"
            >
              Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-2 py-1 rounded border border-retro-border hover:bg-retro-bg disabled:opacity-30"
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/** Single cell value with truncation support for long strings */
function CellValue({ value }: { value: unknown }) {
  const [expanded, setExpanded] = useState(false)

  if (value === null || value === undefined) return <span>-</span>

  const formatted = formatValue(value)
  const isLong = typeof value === 'string' && value.length > MAX_STRING_LENGTH

  if (!isLong || expanded) {
    return (
      <span>
        {formatted}
        {isLong && (
          <button
            onClick={() => setExpanded(false)}
            className="ml-2 text-retro-cyan hover:underline"
            aria-label="Show less"
          >
            less
          </button>
        )}
      </span>
    )
  }

  return (
    <span>
      {value.slice(0, MAX_STRING_LENGTH)}...
      <button
        onClick={() => setExpanded(true)}
        className="ml-2 text-retro-cyan hover:underline"
        aria-label="Show more"
      >
        more
      </button>
    </span>
  )
}

/** Renders a list of primitives as a single-column table */
function PrimitiveList({ data, title }: { data: unknown[]; title: string }) {
  return (
    <div>
      <h4 className="text-xs font-mono uppercase tracking-wider text-retro-amber mb-2">{title}</h4>
      <div className="overflow-x-auto border border-retro-border rounded">
        <table className="w-full text-xs">
          <thead className="bg-retro-bg border-b border-retro-border">
            <tr>
              <th className="px-3 py-2 text-left text-retro-dim uppercase tracking-wider w-12">
                #
              </th>
              <th className="px-3 py-2 text-left text-retro-dim uppercase tracking-wider">
                Value
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-retro-border">
            {data.map((item, index) => (
              <tr key={index} className="hover:bg-retro-bg/50">
                <td className="px-3 py-2 text-retro-dim font-mono">{index + 1}</td>
                <td className="px-3 py-2 text-retro-text font-mono">{formatValue(item)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Renders a 2D array as a grid table */
function MatrixTable({ data, title }: { data: unknown[][]; title: string }) {
  const maxCols = Math.max(...data.map((row) => (Array.isArray(row) ? row.length : 0)))

  return (
    <div>
      <h4 className="text-xs font-mono uppercase tracking-wider text-retro-amber mb-2">{title}</h4>
      <div className="overflow-x-auto border border-retro-border rounded">
        <table className="w-full text-xs">
          <thead className="bg-retro-bg border-b border-retro-border">
            <tr>
              <th className="px-3 py-2 text-left text-retro-dim uppercase tracking-wider w-12">
                Row
              </th>
              {Array.from({ length: maxCols }, (_, i) => (
                <th
                  key={i}
                  className="px-3 py-2 text-left text-retro-dim uppercase tracking-wider"
                >
                  Col {i + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-retro-border">
            {data.map((row, rowIdx) => (
              <tr key={rowIdx} className="hover:bg-retro-bg/50">
                <td className="px-3 py-2 text-retro-dim font-mono">{rowIdx + 1}</td>
                {Array.from({ length: maxCols }, (_, colIdx) => (
                  <td key={colIdx} className="px-3 py-2 text-retro-text font-mono">
                    {Array.isArray(row) && colIdx < row.length
                      ? formatValue(row[colIdx])
                      : '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Renders mixed arrays with each item as its own row/section */
function MixedArrayView({
  data,
  title,
  depth,
}: {
  data: unknown[]
  title: string
  depth: number
}) {
  return (
    <div>
      <h4 className="text-xs font-mono uppercase tracking-wider text-retro-amber mb-2">{title}</h4>
      <div className="overflow-x-auto border border-retro-border rounded">
        <table className="w-full text-xs">
          <thead className="bg-retro-bg border-b border-retro-border">
            <tr>
              <th className="px-3 py-2 text-left text-retro-dim uppercase tracking-wider w-12">
                #
              </th>
              <th className="px-3 py-2 text-left text-retro-dim uppercase tracking-wider">
                Value
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-retro-border">
            {data.map((item, index) => {
              const itemKind = classifyValue(item)
              const isComplex =
                itemKind === 'complex-object' ||
                itemKind === 'simple-object' ||
                itemKind === 'array-of-objects'

              return (
                <tr key={index} className="hover:bg-retro-bg/50 align-top">
                  <td className="px-3 py-2 text-retro-dim font-mono">{index + 1}</td>
                  <td className="px-3 py-2 text-retro-text font-mono">
                    {isComplex ? (
                      <SmartDataView
                        data={item}
                        title={`Item ${index + 1}`}
                        depth={depth + 1}
                      />
                    ) : (
                      formatValue(item)
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Collapsible section for nested complex data */
function CollapsibleSection({
  label,
  defaultExpanded,
  children,
}: {
  label: string
  defaultExpanded: boolean
  children: React.ReactNode
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  return (
    <div className="border border-retro-border rounded">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-wider text-retro-muted hover:bg-retro-bg/50 transition-colors"
        aria-label={label}
        aria-expanded={expanded}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {label}
      </button>
      {expanded && <div className="px-3 py-2 border-t border-retro-border">{children}</div>}
    </div>
  )
}

/** Empty state placeholder */
function EmptyState({ title }: { title: string }) {
  return (
    <div className="border border-retro-border rounded p-4 text-center">
      <h4 className="text-xs font-mono uppercase tracking-wider text-retro-amber mb-1">
        {title}
      </h4>
      <p className="text-xs text-retro-dim">No data available</p>
    </div>
  )
}

export default SmartDataView
